import React, { useMemo } from 'react';
import { FlatList, KeyboardAvoidingView, Modal, Platform, StyleSheet, Text, TextInput, TouchableOpacity, View } from './rn';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import ActivityIndicator from './LoadingIndicator';
import Avatar from './Avatar';
import { fontSize, spacing, ThemeColors, radius } from '../theme';
import { useTheme } from '../theme/ThemeContext';

export type ContactExceptionListItem = {
  id: number;
  name: string;
  surname: string | null;
  photo_url: string | null;
  blocked_by_owner: boolean;
};

type Props<T extends ContactExceptionListItem> = {
  visible: boolean;
  loading: boolean;
  items: T[];
  title: string;
  backLabel: string;
  emptyText: string;
  errorText: string | null;
  retryLabel: string;
  searchText: string;
  searchPlaceholder: string;
  searchEmptyText: string;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreErrorText: string | null;
  loadMoreLabel: string;
  openProfileHint: string;
  testID: string;
  topInset: number;
  statusText: (item: T) => string;
  onRetry: () => void;
  onSearchChange: (value: string) => void;
  onLoadMore: () => void;
  onItemPress: (item: T) => void;
  onClose: () => void;
};

// Arama ve mesaj gizliliği aynı liste geometrisini paylaşır. Veri alanı ve boş
// durum metni çağıran yüzeye aittir; yükleme, kişi satırı ve kapanış davranışı
// burada tek kaynakta kalır.
export default function ContactExceptionsModal<T extends ContactExceptionListItem>({
  visible,
  loading,
  items,
  title,
  backLabel,
  emptyText,
  errorText,
  retryLabel,
  searchText,
  searchPlaceholder,
  searchEmptyText,
  hasMore,
  loadingMore,
  loadMoreErrorText,
  loadMoreLabel,
  openProfileHint,
  testID,
  topInset,
  statusText,
  onRetry,
  onSearchChange,
  onLoadMore,
  onItemPress,
  onClose,
}: Props<T>) {
  const colors = useTheme();
  const insets = useSafeAreaInsets();
  const styles = useMemo(() => makeStyles(colors), [colors]);

  return (
    <Modal
      visible={visible}
      animationType="slide"
      onRequestClose={onClose}
      testID={testID}>
      <KeyboardAvoidingView
        testID={`${testID}-keyboard`}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={[styles.modal, { paddingTop: topInset + spacing.md }]}>
        <View style={styles.header}>
          <Text
            testID={`${testID}-title`}
            style={styles.title}
            accessibilityRole="header"
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.8}>
            {title}
          </Text>
          <TouchableOpacity
            style={styles.backButton}
            onPress={onClose}
            accessibilityRole="button"
            accessibilityLabel={backLabel}>
            <Text style={styles.back}>{backLabel}</Text>
          </TouchableOpacity>
        </View>
        <View style={styles.searchWrap}>
          <TextInput
            testID={`${testID}-search`}
            style={styles.searchInput}
            value={searchText}
            onChangeText={onSearchChange}
            placeholder={searchPlaceholder}
            placeholderTextColor={colors.gray400}
            accessibilityLabel={searchPlaceholder}
            autoCapitalize="none"
            autoCorrect={false}
            returnKeyType="search"
          />
        </View>
        {loading ? (
          <ActivityIndicator color={colors.black} style={styles.loading} />
        ) : errorText ? (
          <View testID={`${testID}-error`} style={styles.feedback} accessibilityLiveRegion="polite">
            <Text style={styles.feedbackText}>{errorText}</Text>
            <TouchableOpacity
              testID={`${testID}-retry`}
              style={styles.retryButton}
              onPress={onRetry}
              accessibilityRole="button"
              accessibilityLabel={retryLabel}>
              <Text style={styles.retryText}>{retryLabel}</Text>
            </TouchableOpacity>
          </View>
        ) : (
          <FlatList
            testID={`${testID}-list`}
            data={items}
            keyExtractor={(item) => String(item.id)}
            keyboardShouldPersistTaps="handled"
            keyboardDismissMode="on-drag"
            contentContainerStyle={[styles.listContent, { paddingBottom: insets.bottom + spacing.md }]}
            scrollIndicatorInsets={{ bottom: insets.bottom }}
            renderItem={({ item }) => {
              const fullName = [item.name, item.surname].filter(Boolean).join(' ');
              const value = statusText(item);
              return (
                <TouchableOpacity
                  testID={`${testID}-contact-${item.id}`}
                  style={styles.row}
                  onPress={() => onItemPress(item)}
                  accessibilityRole="button"
                  accessibilityLabel={`${fullName}, ${value}`}
                  accessibilityHint={openProfileHint}>
                  <Avatar
                    photoUrl={item.photo_url}
                    name={fullName}
                    size={40}
                    style={styles.avatar}
                  />
                  <Text style={styles.name} numberOfLines={1}>{fullName}</Text>
                  <Text
                    testID={`${testID}-contact-${item.id}-status`}
                    style={styles.value}
                    numberOfLines={2}
                    adjustsFontSizeToFit
                    minimumFontScale={0.8}>
                    {value}
                  </Text>
                  <Text style={styles.chevron} accessible={false}>›</Text>
                </TouchableOpacity>
              );
            }}
            ItemSeparatorComponent={() => <View style={styles.separator} />}
            ListEmptyComponent={(
              <Text style={styles.empty}>{searchText.trim() ? searchEmptyText : emptyText}</Text>
            )}
            ListFooterComponent={(
              loadingMore ? (
                <ActivityIndicator color={colors.black} style={styles.footerLoading} />
              ) : loadMoreErrorText ? (
                <View
                  testID={`${testID}-load-more-error`}
                  style={styles.footerFeedback}
                  accessibilityLiveRegion="polite">
                  <Text style={styles.footerFeedbackText}>{loadMoreErrorText}</Text>
                  <TouchableOpacity
                    testID={`${testID}-load-more-retry`}
                    style={styles.loadMoreButton}
                    onPress={onLoadMore}
                    accessibilityRole="button"
                    accessibilityLabel={retryLabel}>
                    <Text style={styles.retryText}>{retryLabel}</Text>
                  </TouchableOpacity>
                </View>
              ) : hasMore ? (
                <TouchableOpacity
                  testID={`${testID}-load-more`}
                  style={styles.loadMoreButton}
                  onPress={onLoadMore}
                  accessibilityRole="button"
                  accessibilityLabel={loadMoreLabel}>
                  <Text style={styles.retryText}>{loadMoreLabel}</Text>
                </TouchableOpacity>
              ) : null
            )}
          />
        )}
      </KeyboardAvoidingView>
    </Modal>
  );
}

function makeStyles(colors: ThemeColors) {
  return StyleSheet.create({
    modal: { flex: 1, backgroundColor: colors.white },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: spacing.lg,
      paddingBottom: spacing.md,
    },
    title: { flex: 1, minWidth: 0, fontSize: fontSize.lg, fontWeight: '700', color: colors.black },
    backButton: {
      minWidth: 44,
      minHeight: 44,
      marginLeft: spacing.md,
      justifyContent: 'center',
      alignItems: 'flex-end',
    },
    back: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
    loading: { marginTop: spacing.lg },
    searchWrap: { paddingHorizontal: spacing.lg, paddingBottom: spacing.sm },
    searchInput: {
      minHeight: 44,
      borderWidth: 1,
      borderColor: colors.gray200,
      borderRadius: radius.control,
      paddingHorizontal: spacing.md,
      fontSize: fontSize.md,
      color: colors.black,
      backgroundColor: colors.white,
    },
    listContent: { paddingHorizontal: spacing.lg },
    row: { flexDirection: 'row', alignItems: 'center', paddingVertical: spacing.md },
    avatar: { width: 40, height: 40, borderRadius: 20, marginRight: spacing.md },
    name: { flex: 1, minWidth: 0, fontSize: fontSize.md, color: colors.black, fontWeight: '500' },
    value: {
      maxWidth: '42%',
      marginLeft: spacing.sm,
      fontSize: fontSize.sm,
      lineHeight: 18,
      textAlign: 'right',
      color: colors.gray600,
    },
    chevron: { marginLeft: spacing.sm, fontSize: fontSize.lg, color: colors.gray400 },
    separator: { height: 1, backgroundColor: colors.gray100 },
    feedback: { alignItems: 'center', paddingHorizontal: spacing.xl, paddingTop: spacing.xl },
    feedbackText: {
      fontSize: fontSize.md,
      lineHeight: 22,
      color: colors.gray600,
      textAlign: 'center',
    },
    retryButton: {
      minHeight: 44,
      marginTop: spacing.md,
      paddingHorizontal: spacing.lg,
      justifyContent: 'center',
    },
    retryText: { fontSize: fontSize.md, fontWeight: '600', color: colors.black },
    footerLoading: { marginVertical: spacing.lg },
    footerFeedback: { alignItems: 'center', paddingVertical: spacing.md },
    footerFeedbackText: { fontSize: fontSize.sm, color: colors.gray600, textAlign: 'center' },
    loadMoreButton: {
      minHeight: 44,
      alignItems: 'center',
      justifyContent: 'center',
      paddingHorizontal: spacing.lg,
      marginVertical: spacing.sm,
    },
    empty: {
      fontSize: fontSize.md,
      color: colors.gray400,
      textAlign: 'center',
      paddingTop: spacing.xl,
    },
  });
}
